import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  FileSpreadsheet, Download, CheckCircle2, FileText, 
  Table2, BarChart3, Droplets, AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ExportPanel = () => {
  const [exportTypes, setExportTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('full');
  const [isExporting, setIsExporting] = useState(false);
  const [exportHistory, setExportHistory] = useState([]);

  useEffect(() => {
    loadExportTypes();
  }, []);

  const loadExportTypes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/export/types`);
      const data = await response.json();
      setExportTypes(data.types || []);
    } catch (err) {
      console.error('Failed to load export types:', err);
      // Fallback types
      setExportTypes([
        { id: 'full', name: 'Full Report', description: 'Complete dashboard data' },
        { id: 'overview', name: 'Overview', description: 'Summary metrics' },
        { id: 'villages', name: 'Villages', description: 'Village data' },
        { id: 'nrw', name: 'NRW Analysis', description: 'NRW breakdown' },
        { id: 'flow', name: 'Flow Hierarchy', description: 'Flow data' },
        { id: 'quality', name: 'Water Quality', description: 'Quality tests' },
      ]);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await fetch(`${API_URL}/api/export/excel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ report_type: selectedType })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const filename = response.headers.get('Content-Disposition')?.split('filename=')[1] || 
                        `swati_report_${selectedType}_${new Date().toISOString().split('T')[0]}.xlsx`;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();

        // Add to history
        setExportHistory(prev => [{
          type: selectedType,
          timestamp: new Date().toLocaleString(),
          status: 'success'
        }, ...prev.slice(0, 4)]);
      } else {
        throw new Error('Export failed');
      }
    } catch (err) {
      console.error('Export error:', err);
      setExportHistory(prev => [{
        type: selectedType,
        timestamp: new Date().toLocaleString(),
        status: 'failed'
      }, ...prev.slice(0, 4)]);
    } finally {
      setIsExporting(false);
    }
  };

  const getTypeIcon = (typeId) => {
    switch (typeId) {
      case 'overview': return <BarChart3 className="w-5 h-5" />;
      case 'villages': return <Table2 className="w-5 h-5" />;
      case 'nrw': return <AlertTriangle className="w-5 h-5" />;
      case 'flow': return <Droplets className="w-5 h-5" />;
      case 'quality': return <CheckCircle2 className="w-5 h-5" />;
      default: return <FileSpreadsheet className="w-5 h-5" />;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5 text-green-600" />
          Export to Excel
        </CardTitle>
        <CardDescription>
          Download dashboard data in Excel format for offline analysis and reporting
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Export Type Selection */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {exportTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => setSelectedType(type.id)}
              className={`p-3 rounded-lg border text-left transition-all ${
                selectedType === type.id 
                  ? 'border-primary bg-primary/5 ring-1 ring-primary' 
                  : 'border-border hover:border-primary/50'
              }`}
              data-testid={`export-type-${type.id}`}
            >
              <div className="flex items-center gap-2 mb-1">
                {getTypeIcon(type.id)}
                <span className="font-medium text-sm">{type.name}</span>
              </div>
              <p className="text-xs text-muted-foreground">{type.description}</p>
            </button>
          ))}
        </div>

        {/* Export Button */}
        <Button 
          onClick={handleExport} 
          disabled={isExporting}
          className="w-full bg-green-600 hover:bg-green-700"
          data-testid="export-excel-btn"
        >
          {isExporting ? (
            <>
              <Download className="w-4 h-4 mr-2 animate-bounce" />
              Generating Excel...
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              Download {exportTypes.find(t => t.id === selectedType)?.name || 'Report'} (.xlsx)
            </>
          )}
        </Button>

        {/* Export History */}
        {exportHistory.length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium mb-2">Recent Exports</p>
            <div className="space-y-2">
              {exportHistory.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm p-2 bg-muted rounded">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    <span className="capitalize">{item.type} Report</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{item.timestamp}</span>
                    <Badge variant={item.status === 'success' ? 'default' : 'destructive'} className="text-xs">
                      {item.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ExportPanel;
